import { useState, useEffect, useCallback } from 'react'
import {
  fetchDailyAttendance,
  saveDailyAttendance,
  syncSwipesForDate,
  syncLecturesForDate,
} from '../api/dailyAttendanceApi'
import { appToast } from '../../../../components/AppToast'

export default function useDailyAttendance() {
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0])
  const [students, setStudents] = useState([])
  const [loading, setLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isReportsOpen, setIsReportsOpen] = useState(false)
  const [sortBy, setSortBy] = useState('rollNo')
  const [sortOrder, setSortOrder] = useState('asc')

  // Filter states
  const [searchText, setSearchText] = useState('')
  const [searchField, setSearchField] = useState('All')
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [courseFilter, setCourseFilter] = useState('All')
  const [schoolFilter, setSchoolFilter] = useState('All')
  const [academicYearFilter, setAcademicYearFilter] = useState('All')
  const [batchFilter, setBatchFilter] = useState('All')
  const [subjectFilter, setSubjectFilter] = useState('All')

  // Metrics
  const [metrics, setMetrics] = useState({
    totalStudents: 0,
    presentCount: 0,
    absentCount: 0,
    holidayCount: 0,
    presentPct: 0,
    absentPct: 0,
  })

  // Load Data
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const filters = {
        searchText,
        searchField,
        company: companyFilter,
        branch: branchFilter,
        course: courseFilter,
        school: schoolFilter,
        academicYear: academicYearFilter,
        batch: batchFilter,
        subject: subjectFilter,
      }
      const res = await fetchDailyAttendance(date, filters, sortBy, sortOrder)
      setStudents(res.students)
      setMetrics(res.metrics)
    } catch (e) {
      console.error('Failed to load daily attendance', e)
      appToast.error('Failed to load student attendance.')
    } finally {
      setLoading(false)
    }
  }, [
    date,
    searchText,
    searchField,
    companyFilter,
    branchFilter,
    courseFilter,
    schoolFilter,
    academicYearFilter,
    batchFilter,
    subjectFilter,
    sortBy,
    sortOrder,
  ])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Recalculate metrics locally on in-memory edit
  const recalculateMetrics = (list) => {
    let presentCount = 0
    let absentCount = 0
    let holidayCount = 0

    list.forEach((s) => {
      if (s.attendance === 'P') presentCount++
      else if (s.attendance === 'A') absentCount++
      else if (s.attendance === 'H') holidayCount++
    })

    const totalStudents = list.length
    const presentPct = totalStudents > 0 ? Math.round((presentCount / totalStudents) * 100) : 0
    const absentPct = totalStudents > 0 ? Math.round((absentCount / totalStudents) * 100) : 0

    setMetrics({
      totalStudents,
      presentCount,
      absentCount,
      holidayCount,
      presentPct,
      absentPct,
    })
  }

  // Row Edit Handlers
  const handleStatusChange = (id, newStatus) => {
    setStudents((prev) => {
      const updated = prev.map((s) => (s.id === id ? { ...s, attendance: newStatus } : s))
      recalculateMetrics(updated)
      return updated
    })
  }

  const handleReasonChange = (id, newReason) => {
    setStudents((prev) =>
      prev.map((s) => (s.id === id ? { ...s, reason: newReason } : s))
    )
  }

  // Mass Attendance Selection
  const handleMarkAll = (status) => {
    setStudents((prev) => {
      const updated = prev.map((s) => ({ ...s, attendance: status }))
      recalculateMetrics(updated)
      return updated
    })
    const label = status === 'P' ? 'Present' : status === 'A' ? 'Absent' : 'Holiday'
    appToast.info(`All students marked as ${label}. Remember to save changes.`)
  }

  // Save Attendance
  const handleSave = async () => {
    setIsSaving(true)
    const map = {}
    students.forEach((s) => {
      map[s.id] = {
        attendance: s.attendance,
        reason: s.reason,
      }
    })
    const res = await saveDailyAttendance(date, map)
    setIsSaving(false)

    if (res.success) {
      appToast.success(`Attendance for ${date} saved successfully!`)
    } else {
      appToast.error(res.error || 'Failed to save attendance.')
    }
  }

  // Read Swipes
  const handleReadSwipes = async () => {
    const updatedMap = await syncSwipesForDate(date, students)
    setStudents((prev) => {
      const updated = prev.map((s) => ({
        ...s,
        attendance: updatedMap[s.id]?.attendance || s.attendance,
        reason: updatedMap[s.id]?.reason || '',
      }))
      recalculateMetrics(updated)
      return updated
    })
    appToast.success('Attendance updated from biometric swipe records!')
  }

  // Read from Lectures
  const handleReadFromLectures = async () => {
    const updatedMap = await syncLecturesForDate(date, students)
    setStudents((prev) => {
      const updated = prev.map((s) => ({
        ...s,
        attendance: updatedMap[s.id]?.attendance || s.attendance,
        reason: updatedMap[s.id]?.reason || '',
      }))
      recalculateMetrics(updated)
      return updated
    })
    appToast.success('Attendance synced from scheduled lectures!')
  }

  // SMS & WhatsApp Dispatches
  const handleSendSMS = () => {
    const absentCount = students.filter((s) => s.attendance === 'A').length
    appToast.info(`Dispatched attendance SMS to parents (${absentCount} absent notices sent).`)
  }

  const handleSendWhatsApp = () => {
    const absentCount = students.filter((s) => s.attendance === 'A').length
    appToast.info(`Dispatched attendance WhatsApp alerts to parents (${absentCount} notices).`)
  }

  // Sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  // Date Navigation Helpers
  const handlePrevDate = () => {
    const current = new Date(date)
    current.setDate(current.getDate() - 1)
    setDate(current.toISOString().split('T')[0])
  }

  const handleNextDate = () => {
    const current = new Date(date)
    current.setDate(current.getDate() + 1)
    setDate(current.toISOString().split('T')[0])
  }

  const handleToday = () => {
    setDate(new Date().toISOString().split('T')[0])
  }

  const handleYesterday = () => {
    const yest = new Date()
    yest.setDate(yest.getDate() - 1)
    setDate(yest.toISOString().split('T')[0])
  }

  // Reset Filters
  const handleResetFilters = () => {
    setSearchText('')
    setSearchField('All')
    setCompanyFilter('All')
    setBranchFilter('All')
    setCourseFilter('All')
    setSchoolFilter('All')
    setAcademicYearFilter('2026-2027')
    setBatchFilter('All')
    setSubjectFilter('All')
    appToast.info('Filters have been reset.')
  }

  // Options
  const options = {
    searchFields: [
      { value: 'All', label: 'All Fields' },
      { value: 'Student', label: 'Student Name' },
      { value: 'RollNo', label: 'Roll Number' },
      { value: 'Course', label: 'Course' },
      { value: 'Batch', label: 'Batch' },
      { value: 'Branch', label: 'Branch' },
      { value: 'Company', label: 'Company' },
    ],
    companies: [
      { value: 'All', label: 'All Companies' },
      { value: 'Tez Educational Trust', label: 'Tez Educational Trust' },
      { value: 'Tez Educational School', label: 'Tez Educational School' },
      { value: 'Tolani College Of Commerce', label: 'Tolani College Of Commerce' },
    ],
    branches: [
      { value: 'All', label: 'All Branches' },
      { value: 'Dadar', label: 'Dadar' },
      { value: 'Thane', label: 'Thane' },
      { value: 'Andheri', label: 'Andheri' },
      { value: 'Borivali', label: 'Borivali' },
    ],
    courses: [
      { value: 'All', label: 'All Courses' },
      { value: 'CA Foundation', label: 'CA Foundation' },
      { value: 'CS Executive', label: 'CS Executive' },
      { value: 'JEE Advanced', label: 'JEE Advanced' },
      { value: 'NEET UG', label: 'NEET UG' },
    ],
    schools: [
      { value: 'All', label: 'All Schools' },
      { value: 'Main Campus', label: 'Main Campus' },
      { value: 'East Annex', label: 'East Annex' },
      { value: 'North Campus', label: 'North Campus' },
    ],
    batches: [
      { value: 'All', label: 'All Batches' },
      { value: 'Batch-A (Morning)', label: 'Batch-A (Morning)' },
      { value: 'Batch-CS-01', label: 'Batch-CS-01' },
      { value: 'Super-30 (Intensive)', label: 'Super-30 (Intensive)' },
      { value: 'Med-Achievers', label: 'Med-Achievers' },
    ],
    subjects: [
      { value: 'All', label: 'All Subjects' },
      { value: 'Principles and Practice of Accounting', label: 'Accounting' },
      { value: 'Company Law & Practice', label: 'Company Law' },
      { value: 'Physics', label: 'Physics' },
      { value: 'Mathematics', label: 'Mathematics' },
      { value: 'Biology (Botany)', label: 'Biology' },
      { value: 'Chemistry', label: 'Chemistry' },
    ],
    academicYears: [
      { value: 'All', label: 'All Years' },
      { value: '2026-2027', label: '2026-2027' },
      { value: '2025-2026', label: '2025-2026' },
    ],
  }

  return {
    state: {
      date,
      students,
      loading,
      isSaving,
      isReportsOpen,
      sortBy,
      sortOrder,
      metrics,
      searchText,
      searchField,
      companyFilter,
      branchFilter,
      courseFilter,
      schoolFilter,
      academicYearFilter,
      batchFilter,
      subjectFilter,
      options,
    },
    actions: {
      setDate,
      setIsReportsOpen,
      setSearchText,
      setSearchField,
      setCompanyFilter,
      setBranchFilter,
      setCourseFilter,
      setSchoolFilter,
      setAcademicYearFilter,
      setBatchFilter,
      setSubjectFilter,
      handleSort,
      handleStatusChange,
      handleReasonChange,
      handleMarkAll,
      handleSave,
      handleReadSwipes,
      handleReadFromLectures,
      handleSendSMS,
      handleSendWhatsApp,
      handlePrevDate,
      handleNextDate,
      handleToday,
      handleYesterday,
      handleResetFilters,
    },
  }
}
